/**
 * Shared attachment staging for Benny chat composers (header BennyChat +
 * Simple Mode). Pure client-side helpers only — no React, no toasts: callers
 * own their own state and surface `error` however their surface does.
 *
 * The rules here MIRROR the server's validation on POST /ai/assistant/chat/stream
 * (4 files, images by `image/*`, a fixed document set, 4.5 MB per document,
 * 20 MB total decoded). Keeping them in sync means a rejection is a friendly
 * toast instead of a 400 after the upload has already been paid for.
 */

import type { AssistantChatAttachment } from '@/lib/api/assistant.api'

/**
 * Pending attachment bound to a local preview URL. Kept on the optimistic
 * message until the request resolves, then dropped — we don't persist
 * client-side previews.
 */
export interface PendingAttachment extends AssistantChatAttachment {
  id: string
  /** object URL for thumbnail rendering — must be revoked when removed. */
  previewUrl: string
  size: number
  kind: 'image' | 'document'
}

export const MAX_ATTACHMENTS = 4
/** Per-image ceiling. Below the server's total budget, kept as a UX guard. */
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024 // 5 MB
/** Bedrock Converse caps each document block at 4.5 MB — server enforces too. */
export const MAX_DOCUMENT_BYTES = 4.5 * 1024 * 1024
/** Server's decoded ceiling across every attachment on one message. */
export const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024

/** Content types the server accepts as documents (images match `image/*`). */
const DOCUMENT_CONTENT_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
])

/**
 * Extension → content type. Browsers are unreliable here: `.md` usually
 * arrives as `text/plain` or an empty string, `.csv` sometimes as
 * `application/vnd.ms-excel`. The extension wins when we recognise it, so the
 * server sees a content type from its accepted set.
 */
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  md: 'text/markdown',
  markdown: 'text/markdown',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

/** File-picker `accept` string covering everything the server takes. */
export const ATTACHMENT_ACCEPT = 'image/*,.pdf,.txt,.csv,.md,.docx,.xlsx'

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : ''
}

/**
 * Resolve the content type we'll actually send. Falls back to the extension
 * map when the browser's type is missing or outside the accepted sets.
 */
function resolveContentType(file: File): string | null {
  const byExtension = EXTENSION_CONTENT_TYPES[extensionOf(file.name)]
  if (byExtension) return byExtension
  if (file.type.startsWith('image/')) return file.type
  if (DOCUMENT_CONTENT_TYPES.has(file.type)) return file.type
  return null
}

/**
 * Read a File into a base64-encoded `AssistantChatAttachment`. Strips the
 * `data:<mime>;base64,` prefix so the backend gets clean bytes.
 */
export async function fileToAttachment(file: File): Promise<PendingAttachment> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () =>
      reject(reader.error || new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
  const base64 = dataUrl.includes(',')
    ? (dataUrl.split(',', 2)[1] ?? '')
    : dataUrl
  const contentType =
    resolveContentType(file) || file.type || 'application/octet-stream'
  return {
    id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    contentType,
    base64,
    fileName: file.name,
    previewUrl: URL.createObjectURL(file),
    size: file.size,
    kind: contentType.startsWith('image/') ? 'image' : 'document',
  }
}

/** 'image' keeps a composer image-only; 'any' also takes documents. */
export type AttachmentMode = 'image' | 'any'

export interface AcceptAttachmentsResult {
  accepted: PendingAttachment[]
  /** First rejection reason, if any — surface as a single toast. */
  error?: string
}

function formatMb(bytes: number): string {
  return `${Number.isInteger(bytes / 1024 / 1024) ? bytes / 1024 / 1024 : (bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Validate + read a batch of dropped/pasted/picked files against `existing`.
 * Everything acceptable is returned; the first rejection reason comes back as
 * `error` so the caller raises exactly one toast per batch.
 */
export async function acceptAttachmentFiles(
  files: File[] | FileList,
  existing: PendingAttachment[],
  mode: AttachmentMode = 'any',
): Promise<AcceptAttachmentsResult> {
  const all = Array.from(files)
  if (all.length === 0) return { accepted: [] }

  let error: string | undefined
  const reject = (reason: string) => {
    if (!error) error = reason
  }

  const incoming = all.filter(file => {
    const contentType = resolveContentType(file)
    if (!contentType) return false
    return mode === 'image' ? contentType.startsWith('image/') : true
  })
  if (incoming.length === 0) {
    return {
      accepted: [],
      error:
        mode === 'image'
          ? 'Only image attachments are supported.'
          : 'Only images and PDF, TXT, CSV, MD, DOCX or XLSX files can be attached.',
    }
  }
  if (incoming.length < all.length) {
    reject(
      mode === 'image'
        ? 'Only image attachments are supported.'
        : 'Only images and PDF, TXT, CSV, MD, DOCX or XLSX files can be attached.',
    )
  }

  const room = MAX_ATTACHMENTS - existing.length
  if (room <= 0) {
    return {
      accepted: [],
      error: `Up to ${MAX_ATTACHMENTS} attachments per message.`,
    }
  }
  if (incoming.length > room) {
    reject(`Up to ${MAX_ATTACHMENTS} attachments per message.`)
  }

  let totalBytes = existing.reduce((sum, att) => sum + att.size, 0)
  const accepted: PendingAttachment[] = []
  for (const file of incoming.slice(0, room)) {
    const isImage = (resolveContentType(file) ?? '').startsWith('image/')
    const limit = isImage ? MAX_ATTACHMENT_BYTES : MAX_DOCUMENT_BYTES
    if (file.size > limit) {
      reject(`${file.name} is larger than ${formatMb(limit)}. Skipped.`)
      continue
    }
    if (totalBytes + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
      reject(
        `Attachments exceed ${formatMb(MAX_TOTAL_ATTACHMENT_BYTES)} in total.`,
      )
      continue
    }
    try {
      accepted.push(await fileToAttachment(file))
      totalBytes += file.size
    } catch {
      reject(`Could not read ${file.name}.`)
    }
  }

  return error ? { accepted, error } : { accepted }
}

/** Strip the client-only fields before putting attachments on the wire. */
export function toRequestAttachments(
  attachments: PendingAttachment[],
): AssistantChatAttachment[] {
  return attachments.map(({ contentType, base64, fileName }) => ({
    contentType,
    base64,
    ...(fileName ? { fileName } : {}),
  }))
}
